/**
 * 端末内ストレージ（IndexedDB、§7.4）。
 * track_points / applications / day_state はサーバに送らない（day_stateは集計値のみ送信）。
 */
import type {
  Application,
  CareEntry,
  DailyAggregate,
  GpsFix,
  Product,
  SkinAgeReading,
  UserAccount,
} from "@uvk/shared";

const DB_NAME = "uvk";
const DB_VERSION = 4; // v2: care_log / v3: skin_age / v4: users（端末内アカウント）

export interface DayStateRow extends DailyAggregate {
  /** 分単位内訳（画面表示用、送信しない） */
  minUnknown: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("track_points")) {
        db.createObjectStore("track_points", { keyPath: "ts" });
      }
      if (!db.objectStoreNames.contains("applications")) {
        db.createObjectStore("applications", { keyPath: "ts" });
      }
      if (!db.objectStoreNames.contains("day_state")) {
        db.createObjectStore("day_state", { keyPath: "date" });
      }
      if (!db.objectStoreNames.contains("products")) {
        db.createObjectStore("products", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("terrain")) {
        db.createObjectStore("terrain"); // key = タイル名
      }
      if (!db.objectStoreNames.contains("kv")) {
        db.createObjectStore("kv"); // 設定・気象キャッシュ等
      }
      if (!db.objectStoreNames.contains("care_log")) {
        db.createObjectStore("care_log", { keyPath: "ts" }); // 顔写真を含むため端末内のみ
      }
      if (!db.objectStoreNames.contains("skin_age")) {
        db.createObjectStore("skin_age", { keyPath: "ts" }); // 肌年齢の実測（校正用）
      }
      if (!db.objectStoreNames.contains("users")) {
        // 端末内アカウント（accounts.ts）。パスワードは塩＋ハッシュのみ。サーバには作らない
        db.createObjectStore("users", { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export const db = {
  put: (store: string, value: unknown, key?: IDBValidKey) =>
    tx(store, "readwrite", (s) => s.put(value as never, key)),
  get: <T>(store: string, key: IDBValidKey) => tx<T | undefined>(store, "readonly", (s) => s.get(key) as IDBRequest<T | undefined>),
  getAll: <T>(store: string) => tx<T[]>(store, "readonly", (s) => s.getAll() as IDBRequest<T[]>),
  delete: (store: string, key: IDBValidKey) => tx(store, "readwrite", (s) => s.delete(key)),
  clear: (store: string) => tx(store, "readwrite", (s) => s.clear()),
};

export async function saveFix(fix: GpsFix): Promise<void> {
  await db.put("track_points", fix);
}

export async function saveApplication(app: Application): Promise<void> {
  await db.put("applications", app);
}

export async function listProducts(): Promise<Product[]> {
  return db.getAll<Product>("products");
}

export async function saveDayState(row: DayStateRow): Promise<void> {
  await db.put("day_state", row);
}

export async function listDayStates(): Promise<DayStateRow[]> {
  return db.getAll<DayStateRow>("day_state");
}

/** 軌跡は当日分＋7日で自動削除（§7.4・§11.1） */
export async function pruneTrackPoints(now: number): Promise<void> {
  const cutoff = now - 8 * 86400000;
  const all = await db.getAll<GpsFix>("track_points");
  for (const p of all) if (p.ts < cutoff) await db.delete("track_points", p.ts);
}

// ---- 端末内アカウント（accounts.ts） --------------------------------------

export async function listUsers(): Promise<UserAccount[]> {
  const users = await db.getAll<UserAccount>("users");
  return users.sort((a, b) => a.createdAt - b.createdAt);
}

export async function saveUser(user: UserAccount): Promise<void> {
  await db.put("users", user);
}

/**
 * ユーザーを消す。そのユーザーのケア記録・肌年齢の実測も一緒に消す。
 * 「アカウントは消えたのに記録が残る」状態を作らないため、まとめてここで面倒を見る。
 */
export async function deleteUser(userId: string): Promise<void> {
  for (const entry of await db.getAll<CareEntry>("care_log")) {
    if (entry.userId === userId) await db.delete("care_log", entry.ts);
  }
  for (const r of await db.getAll<SkinAgeReading>("skin_age")) {
    if (r.userId === userId) await db.delete("skin_age", r.ts);
  }
  await db.delete("users", userId);
}

/**
 * アカウントを作る前に残っていた記録を、最初のユーザーに引き取ってもらう。
 * これをしないと、アカウントを入れた瞬間に過去の記録が画面から消えたように見える。
 * 引き取るのは userId が無いものだけ（他人の記録は動かさない）。
 */
export async function adoptOrphanRecords(userId: string): Promise<number> {
  let moved = 0;
  for (const entry of await db.getAll<CareEntry>("care_log")) {
    if (!entry.userId) {
      await db.put("care_log", { ...entry, userId });
      moved += 1;
    }
  }
  for (const r of await db.getAll<SkinAgeReading>("skin_age")) {
    if (!r.userId) {
      await db.put("skin_age", { ...r, userId });
      moved += 1;
    }
  }
  return moved;
}

/** 端末側リセット（§11.3 設定画面の全削除手順） */
export async function wipeLocal(): Promise<void> {
  for (const s of [
    "track_points", "applications", "day_state", "products", "terrain", "kv",
    "care_log", "skin_age", "users",
  ]) {
    await db.clear(s);
  }
  localStorage.clear();
}
